import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  ArrowLeft,
  Maximize2,
  Minimize2,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  Loader2
} from "lucide-react";
import { motion } from "framer-motion";

export default function BookReader() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const res = await api.get(`/books/${bookId}`);
        setBook(res.data);
      } catch (err) {
        console.error("Failed to load book");
      } finally {
        setLoading(false);
      }
    };
    fetchBook();

    // Disable right-click to discourage downloading
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);
    return () => document.removeEventListener("contextmenu", handleContextMenu);
  }, [bookId]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-slate-900">
      <Loader2 className="text-indigo-500 animate-spin" size={48} />
    </div>
  );

  if (!book || !book.pdf_url) return (
    <div className="h-screen flex flex-col items-center justify-center bg-slate-900 text-white space-y-6">
      <ShieldAlert size={64} className="text-rose-500" />
      <h2 className="text-2xl font-black">Book Content Unavailable</h2>
      <button onClick={() => navigate(-1)} className="px-8 py-3 bg-white text-slate-900 font-black rounded-2xl">Go Back</button>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-slate-900 overflow-hidden font-['Inter',sans-serif]">
      {/* ─── READER HEADER ─── */}
      <header className="px-6 py-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between z-10 shadow-2xl">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate(-1)}
            className="p-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-white font-black text-sm tracking-tight">{book.title}</h1>
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-0.5">By {book.author} • SECURE READER</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-amber-500/10 text-amber-500 rounded-lg border border-amber-500/20">
            <ShieldAlert size={14} />
            <span className="text-[10px] font-black uppercase tracking-widest">Protected Mode</span>
          </div>
          <button
            onClick={toggleFullscreen}
            className="p-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all"
          >
            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </button>
        </div>
      </header>

      {/* ─── PDF VIEWER AREA ─── */}
      <main className="flex-1 relative bg-slate-800 overflow-hidden select-none">
        {/* We use #toolbar=0 to hide the standard PDF toolbar (including download button) */}
        <iframe
          src={`${book.pdf_url}#toolbar=0&navpanes=0&scrollbar=0`}
          className="w-full h-full border-none pointer-events-auto"
          title={book.title}
        />

        {/* Protection Overlay (Hides context menu and some interactions) */}
        <div className="absolute inset-0 pointer-events-none border-[12px] border-slate-900/50" />
      </main>

      {/* ─── READER FOOTER ─── */}
      <footer className="px-6 py-4 bg-slate-900 border-t border-slate-800 flex items-center justify-center gap-8 z-10">
        <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest">
          Use Browser Controls or Mouse Wheel to scroll
        </div>
      </footer>
    </div>
  );
}
